import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DownloadOutlined, EditOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

/* ============================================================
   PPT 成果面板（嵌在成果弹窗里）
   逐页成片大图 · 对哪页不满意直接写修改意见一键重生成 · 一键导出 pptx
   调用与 PPT 工作室完全同源（后台零改动）。
   ============================================================ */

interface Slide {
  id: string;
  no?: number;
  title?: string;
  subtitle?: string;
  bullets?: string[];
  prompt?: string;
  text?: string;
  textItems?: any[];
  status?: string;
  assetId?: string;
  updatedAt?: string;
  generation?: { status?: string; phaseText?: string; progress?: number };
}

const isDone = (s: Slide) => ['image_done', 'preview_done'].includes(s.status || '');
const isBusy = (s: Slide) => s.status === 'image_generating' || s.generation?.status === 'running';

const PptResultPanel: React.FC<{ pptProjectId: string; title?: string }> = ({ pptProjectId, title }) => {
  const [slides, setSlides] = useState<Slide[] | null>(null);
  const [fbFor, setFbFor] = useState<string | null>(null);
  const [fbText, setFbText] = useState('');
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState('');
  const authToken = useAuthStore((s) => s.token) || '';
  const aliveRef = useRef(true);

  const pull = useCallback(async () => {
    try {
      const { data: st } = await api.get(`/ppt-projects/${pptProjectId}/full-state`);
      const arr: Slide[] = st?.slides || st?.data?.slides || [];
      if (aliveRef.current) setSlides(Array.isArray(arr) ? arr : []);
      return arr;
    } catch { return null; }
  }, [pptProjectId]);

  /* 首拉 + 有页面在生成时轮询 */
  useEffect(() => {
    aliveRef.current = true;
    pull();
    const t = setInterval(async () => {
      const arr = await pull();
      if (arr && !arr.some(isBusy) && busyIds.size === 0) { /* 静止时也保持低频对账 */ }
    }, 3500);
    return () => { aliveRef.current = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pptProjectId]);

  const imgUrl = (s: Slide) =>
    `/api/ppt-assets/${s.assetId}/view?token=${encodeURIComponent(authToken)}&v=${encodeURIComponent(s.updatedAt || s.assetId || '')}`;

  /** 按领导修改意见单页重生成（与 PPT 工作室同一接口） */
  const regenerate = useCallback(async (s: Slide) => {
    const feedback = fbText.trim();
    if (!feedback) return;
    setErr('');
    setBusyIds((prev) => new Set(prev).add(s.id));
    setFbFor(null);
    setFbText('');
    try {
      await api.post(`/ppt-projects/${pptProjectId}/slides/${s.id}/generate-image`, {
        slidePatch: {
          no: s.no,
          title: s.title,
          subtitle: s.subtitle,
          bullets: s.bullets,
          text: s.text,
          textItems: s.textItems,
          prompt: `${s.prompt || ''}\n\n【本次修改要求，必须严格遵循】${feedback}`,
          status: 'prompt_ready',
        },
      });
      /* 轮询直到该页完成 */
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const arr = await pull();
        const cur = arr?.find((x) => x.id === s.id);
        if (cur && (isDone(cur) || cur.status === 'error')) break;
        if (!aliveRef.current) return;
      }
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || '重新生成失败');
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(s.id); return n; });
    }
  }, [fbText, pptProjectId, pull]);

  /** 导出 pptx（build → 下载票证 → 浏览器下载） */
  const exportPptx = useCallback(async () => {
    if (!slides?.length || exporting) return;
    setExporting(true);
    setErr('');
    try {
      await api.post(`/ppt-projects/${pptProjectId}/build-pptx`, { selectedSlideIds: slides.map((s) => s.id) });
      const { data } = await api.post(`/ppt-projects/${pptProjectId}/download-ticket`, {});
      if (!data?.url) throw new Error('未取得下载地址');
      const a = document.createElement('a');
      a.href = data.url;
      a.download = `${title || 'PPT'}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || '导出失败');
    } finally {
      setExporting(false);
    }
  }, [slides, exporting, pptProjectId, title]);

  return (
    <div className="aco26-ppt">
      <div className="bar">
        <b>PPT 成片 · {slides ? `${slides.filter(isDone).length}/${slides.length} 页就绪` : '装载中'}</b>
        <span>对哪页不满意，点那页的「改这页」写意见即可重新生成</span>
        <button className="exp" disabled={exporting || !slides?.length} onClick={exportPptx}>
          {exporting ? <LoadingOutlined /> : <DownloadOutlined />} 导出 PPT
        </button>
      </div>
      {err && <div className="err">{err}</div>}
      <div className="pages">
        {slides === null && <div className="ld"><LoadingOutlined /> 正在装载成片…</div>}
        {slides?.map((s, i) => {
          const busy = isBusy(s) || busyIds.has(s.id);
          return (
            <div key={s.id || i} className={`page ${busy ? 'busy' : ''}`}>
              <div className="canvas">
                {busy && (
                  <div className="mask">
                    <LoadingOutlined />
                    <em>{s.generation?.phaseText || '正在重新绘制这一页…'}</em>
                  </div>
                )}
                {s.assetId ? (
                  <img src={imgUrl(s)} alt="" loading="lazy" draggable={false} />
                ) : (
                  <div className="outline">
                    <b>{s.title || `第 ${s.no ?? i + 1} 页`}</b>
                    {s.subtitle && <span>{s.subtitle}</span>}
                    {s.status === 'error' && <u>这一页出图失败，写条意见重试即可</u>}
                  </div>
                )}
              </div>
              <div className="foot">
                <em>第 {s.no ?? i + 1} 页</em>
                <b>{(s.title || '').slice(0, 18)}</b>
                <button className="edit" disabled={busy} onClick={() => { setFbFor(fbFor === s.id ? null : s.id); setFbText(''); }}>
                  <EditOutlined /> 改这页
                </button>
              </div>
              {fbFor === s.id && (
                <div className="fb">
                  <textarea
                    autoFocus
                    rows={2}
                    value={fbText}
                    placeholder="用大白话写修改意见，例如：标题换成蓝色，图表放大，去掉右下角装饰…"
                    onChange={(e) => setFbText(e.target.value)}
                  />
                  <button className="go" disabled={!fbText.trim()} onClick={() => regenerate(s)}>
                    <ReloadOutlined /> 重新生成这一页
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PptResultPanel;
